import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, map, of, Subject, switchMap, timer } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { DialogService, ProgressDialogComponent } from '@blaze-shared/shared-feature';
import { isBulkActionWorkflow } from '@blaze-shared/workflow-domain';

import { getOverlayIds } from '../+state/elements/elements.selectors';

@Injectable({ providedIn: 'root' })
export class PendingBulkActionService {
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private dialogService = inject(DialogService);

  private progressDialog?: MatDialogRef<ProgressDialogComponent>;
  private dialogTriggerAction = new Subject<'show' | 'hide'>();

  constructor() {
    this.dialogTriggerAction
      .pipe(
        switchMap((action) => {
          // The mechanism calls the `show` function with a 400ms delay
          // to avoid flickering if the operation completes quickly
          if (action === 'show') {
            return timer(400).pipe(map(() => action));
          }
          return of(action);
        }),
      )
      .subscribe((action) => {
        if (action === 'show') {
          this.openBulkActionPendingProgressDialog();
        } else if (action === 'hide') {
          this.closeBulkActionPendingProgressDialog();
        }
      });

    combineLatest([
      this.store.select(runtimeSelectors.selectWorkflowsStatus),
      this.store.select(runtimeSelectors.selectWorkflows),
      this.dialogService.dialogOpened$$,
      this.store.select(getOverlayIds),
    ]).subscribe(([workflowStatuses, workflows, dialogOpened, overlays]) => {
      const shouldHide = dialogOpened || overlays.length;
      const pendingBulkActions = Object.entries(workflowStatuses).filter(([wfId, status]) => {
        const workflow = workflows.find((workflow) => workflow.id === wfId);
        return status?.phase === 'Running' && workflow && isBulkActionWorkflow(workflow);
      });
      if (pendingBulkActions.length && !shouldHide) {
        this.dialogTriggerAction.next('show');
      } else {
        this.dialogTriggerAction.next('hide');
      }
    });
  }

  openBulkActionPendingProgressDialog(): void {
    if (this.progressDialog) {
      return;
    }
    this.progressDialog = this.dialog.open(ProgressDialogComponent, {
      width: 'auto',
      height: 'auto',
      hasBackdrop: true,
      panelClass: ['bd', 'bd-dialog', 'with-theme'],
      minWidth: '184px',
      disableClose: true,
      data: {
        allowCancel: false,
        description: 'Please wait',
        title: '',
        taskId: 'TableBulkAction',
        mode: 'theme',
      },
    });
  }

  closeBulkActionPendingProgressDialog(): void {
    if (this.progressDialog?.componentInstance.taskId === 'TableBulkAction') {
      this.progressDialog?.close();
      this.progressDialog = undefined;
    }
  }
}
